<template>
  <div class="d-flex flex-column min-vh-100">
    <PriorHeader />

    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-8 mt-8">
            <h1 class="text-primary weight-600">Import CMM Submission</h1>
            <p class="text-grey mt-2">Enter the CoverMyMeds submission details and upload supporting documents.</p>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-text-field
                  v-model="submissionKey"
                  label="CMM Submission Key"
                  placeholder="Enter the CoverMyMeds submission key"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="patientLastName"
                  label="Patient Last Name"
                  placeholder="Enter patient last name"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="patientDob"
                  label="Patient Date of Birth"
                  type="date"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <h3 class="text-primary weight-600 mb-2">Upload Documents</h3>
                <div
                  v-for="(doc, index) in supplementalDocs"
                  :key="index"
                  class="mb-3 pa-3 rounded-lg d-flex align-center"
                  style="border: 1px solid #e0e0e0">
                  <input
                    :ref="(el) => (fileInputRefs[index] = el)"
                    type="file"
                    accept=".pdf"
                    style="display: none"
                    @change="handleFileSelect(index, $event)" />
                  <v-icon class="mr-3 flex-shrink-0" :color="doc.links.length ? 'success' : 'grey'">
                    {{ doc.links.length ? "mdi-check-circle" : "mdi-file-outline" }}
                  </v-icon>
                  <div class="flex-grow-1" style="min-width: 0">
                    <v-text-field
                      v-model="doc.label"
                      placeholder="No file selected"
                      variant="underlined"
                      density="compact"
                      hide-details
                      class="text-body-2" />
                    <div v-if="doc.error" class="text-caption text-error">{{ doc.error }}</div>
                  </div>
                  <v-btn
                    v-if="doc.links.length"
                    :href="doc.links[0]"
                    target="_blank"
                    icon="mdi-download"
                    variant="text"
                    size="small"
                    color="primary"
                    class="ml-1 flex-shrink-0" />
                  <v-btn
                    v-else
                    variant="tonal"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-upload"
                    class="ml-2 flex-shrink-0"
                    :loading="doc.uploading"
                    @click="fileInputRefs[index]?.click()">
                    Upload
                  </v-btn>
                  <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    color="error"
                    class="ml-1 flex-shrink-0"
                    @click="supplementalDocs.splice(index, 1)" />
                </div>
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-plus"
                  class="mb-4"
                  @click="supplementalDocs.push({ label: null, links: [], file: null, uploading: false, error: null })">
                  Add Supplemental Document
                </v-btn>

                <v-btn
                  color="primary"
                  block
                  size="large"
                  type="submit"
                  :loading="isSubmitting"
                  :disabled="isSubmitting">
                  Import Submission
                </v-btn>

                <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mt-4">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>

                <v-alert v-if="successMessage" type="success" variant="tonal" class="mt-4">
                  {{ successMessage }}
                </v-alert>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <PriorFooter />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import PriorHeader from "@/components/PriorHeader";
import PriorFooter from "@/components/PriorFooter";
import { orderInfoService } from "@/services/orderInfoService";
import { uploadService } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
import { uuid } from "vue-uuid";

const router = useRouter();
const formRef = ref(null);

const submissionKey = ref("");
const patientLastName = ref("");
const patientDob = ref("");
const supplementalDocs = ref([{ label: null, links: [], file: null, uploading: false, error: null }]);
const fileInputRefs = ref({});
const isSubmitting = ref(false);
const errors = ref([]);
const successMessage = ref(null);
const allowedFileTypes = ["application/pdf"];

const rules = {
  required: (v) => !!v || "This field is required",
};

const handleFileSelect = async (index, event) => {
  const doc = supplementalDocs.value[index];
  const file = event.target.files[0];
  doc.error = null;
  if (!file) return;
  if (!allowedFileTypes.includes(file.type)) {
    doc.error = "Invalid file type. Please upload a PDF file.";
    event.target.value = "";
    return;
  }
  if (file.size > 10485760) {
    doc.error = "File is too large. Maximum file size is 10MB.";
    event.target.value = "";
    return;
  }
  const uploadKey = uuid.v4() + "/" + file.name;
  doc.uploading = true;
  try {
    const downloadLink = await uploadService.uploadFile(file, uploadKey);
    doc.links = [downloadLink];
    doc.label = file.name;
    doc.error = null;
  } catch (error) {
    const apiError = error?.error?.response?.data?.error;
    doc.error = apiError || error?.message || "File upload failed. Please try again.";
    event.target.value = "";
  } finally {
    doc.uploading = false;
  }
};

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) return;

  errors.value = [];
  successMessage.value = null;
  isSubmitting.value = true;

  try {
    const uploadedDocs = supplementalDocs.value
      .filter((doc) => doc.links.length > 0)
      .map((doc) => ({ label: doc.label, links: doc.links, context: "" }));
    const response = await orderInfoService.importCMMSubmission({
      submission_key: submissionKey.value,
      patient_last_name: patientLastName.value,
      patient_dob: patientDob.value,
      patient_chart: uploadedDocs,
    });
    const orderKey = response?.orderKey || response?.order_key;
    successMessage.value = "Submission imported successfully. Redirecting...";

    setTimeout(() => {
      router.push({ name: "check-my-coverage", params: { id: orderKey } });
    }, 1500);
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    if (errors.value.length === 0) {
      errors.value = ["An unexpected error occurred. Please try again."];
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>
